import { T, useGT } from 'gt-react';
import {
  formatReportNumber,
  reportNumberFormatSchema,
  type ReportNumberFormat,
} from '@erp/shared-types';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

const CURRENCIES = ['USD', 'CAD', 'MXN', 'EUR', 'GBP', 'AUD', 'JPY'] as const;
const AUTO = 'auto';

/**
 * A number format's fields, as a spreadsheet's Format Cells offers them:
 * Number, Currency or Percent, decimals, and compact; with a sample of how
 * 1234.5 (or 0.256 for Percent) reads.
 */
export function NumberFormatFields({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: Partial<ReportNumberFormat> | undefined;
  onChange: (format: ReportNumberFormat) => void;
}) {
  const gt = useGT();
  const format = reportNumberFormatSchema.parse(value ?? {});
  const update = (patch: Partial<ReportNumberFormat>) =>
    onChange({ ...format, ...patch });
  const sample = formatReportNumber(
    format.style === 'percent' ? 0.256 : 1234.5,
    format,
  );
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Label htmlFor={`${idPrefix}-style`} className="text-xs">
            <T context="number format style label">Format</T>
          </Label>
          <Select
            value={format.style}
            onValueChange={(style) =>
              update({
                style: reportNumberFormatSchema.shape.style.parse(style),
              })
            }
          >
            <SelectTrigger id={`${idPrefix}-style`} className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="number">
                <T context="number format style">Number</T>
              </SelectItem>
              <SelectItem value="currency">
                <T context="number format style">Currency</T>
              </SelectItem>
              <SelectItem value="percent">
                <T context="number format style">Percent</T>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-24 space-y-1.5">
          <Label htmlFor={`${idPrefix}-decimals`} className="text-xs">
            <T context="number format decimals label">Decimals</T>
          </Label>
          <Select
            value={
              format.decimals === undefined ? AUTO : String(format.decimals)
            }
            onValueChange={(next) =>
              update({ decimals: next === AUTO ? undefined : Number(next) })
            }
          >
            <SelectTrigger id={`${idPrefix}-decimals`} className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO}>
                <T context="number format decimals automatic">Auto</T>
              </SelectItem>
              {[0, 1, 2, 3, 4].map((digits) => (
                <SelectItem key={digits} value={String(digits)}>
                  {digits}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {format.style === 'currency' && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-currency`} className="text-xs">
            <T context="number format currency label">Currency</T>
          </Label>
          <Select
            value={format.currency}
            onValueChange={(currency) => update({ currency })}
          >
            <SelectTrigger id={`${idPrefix}-currency`} className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex items-center justify-between">
        <Label htmlFor={`${idPrefix}-compact`} className="text-xs">
          <T context="number format compact toggle">Compact (1.2K, 3.4M)</T>
        </Label>
        <Switch
          id={`${idPrefix}-compact`}
          checked={format.compact}
          onCheckedChange={(compact) => update({ compact })}
        />
      </div>
      <p
        className="text-xs text-muted-foreground"
        aria-label={gt('Format sample', { context: 'number format sample' })}
      >
        {sample}
      </p>
    </div>
  );
}
